// @flow

import * as React from 'react'
import { connect, type Connector } from 'react-redux'
import { Button, Dimmer, Loader, Grid, Container, Header, Popup } from 'semantic-ui-react'

import GhostCampaignCard from './ghost-campaign-card'
import CampaignCard from './campaign-card'
import {
  getCampaigns,
  changeView,
  createCampaign,
  dropCampaignRequest,
  dropCampaignCancel,
  dropCampaign,
  setActiveFilter,
} from '../store/campaigns'
import Modal from '../systemwide/modal'
import { isAdmin, isViewer, getUsername, getSystemUsers } from '../store/authentication'

import './campaigns-grid.css'
import type { ReduxState, Dispatch } from '../store'
import type { User } from '../store/manage-users'

const PopupButton = props => {
  let _props = { ...props, content: undefined }
  return <Popup trigger={<Button {..._props} />} inverted size="mini" content={props.content} position="right center" />
}

type OwnProps = {||}

type MappedProps = {|
  campaigns: $PropertyType<$PropertyType<ReduxState, 'campaigns'>, 'data'>,
  isAdmin: boolean,
  isViewer: boolean,
  currentUser: string,
  isLoading: boolean,
  dropPending: string,
  editPending: string,
  viewType: $PropertyType<$PropertyType<ReduxState, 'campaigns'>, 'viewType'>,
  activesOnly: boolean,
  systemUsers: Array<User>,
|}

const mapStateToProps = (state: ReduxState, ownProps: OwnProps): MappedProps => {
  return {
    campaigns: state.campaigns.data,
    isAdmin: isAdmin(state),
    isViewer: isViewer(state),
    currentUser: getUsername(state),
    isLoading: state.campaigns.status === 'PENDING',
    dropPending: state.campaigns.dropCampaignPending,
    editPending: state.campaigns.editCampaignPending,
    viewType: state.campaigns.viewType,
    activesOnly: isAdmin ? state.campaigns.filterActiveCampaigns : true,
    systemUsers: getSystemUsers(state),
  }
}

type Props = { dispatch: Dispatch } & OwnProps & MappedProps

class CampaignGrid extends React.Component<Props> {
  static defaultProps = {}

  onCommit = value => {
    this.props.dispatch(createCampaign(value.trim()))
  }

  onDeleteRequest = campaignName => {
    this.props.dispatch(dropCampaignRequest(campaignName))
  }

  onDeleteCancel = () => {
    this.props.dispatch(dropCampaignCancel())
  }

  onDeleteConfirm = () => {
    this.props.dispatch(dropCampaign(this.props.dropPending))
  }

  refreshCampaigns = () => {
    this.props.dispatch(getCampaigns())
  }

  changeGridView = viewType => {
    this.props.dispatch(changeView(viewType))
  }

  setActiveFilterHandler = activesOnly => {
    this.props.dispatch(setActiveFilter(activesOnly))
  }

  render() {
    let cards = []
    for (let c of this.props.campaigns) {
      if (this.props.activesOnly && !c.active) continue
      cards.push(
        <Grid.Column key={'campaign-' + c.name}>
          <CampaignCard
            isAdmin={this.props.isAdmin}
            isViewer={this.props.isViewer}
            currentUser={this.props.currentUser}
            campaignName={c.name}
            summary={c.summary}
            waves={c.waves}
            onDelete={this.onDeleteRequest}
            isActive={c.active}
            isPending={this.props.editPending === c.name}
            users={c.users}
            viewType={this.props.viewType}
            systemUsers={this.props.systemUsers}
          />
        </Grid.Column>
      )
    }

    let columns = 0
    switch (this.props.viewType) {
      case 'list':
        columns = 1
        break
      case 'block':
        columns = 2
        break
      default:
        columns = 3
    }

    return (
      <div>
        {/* VERTICAL TOOLBAR */}
        <div style={{ position: 'fixed', paddingLeft: '2ex', paddingTop: '9ex' }}>
          <div>
            <PopupButton primary content="Aggiorna" icon="refresh" onClick={this.refreshCampaigns} />
          </div>
          <div style={{ marginTop: '2ex' }}>
            <Button.Group vertical>
              <PopupButton
                icon="list layout"
                active={this.props.viewType === 'list'}
                onClick={e => this.changeGridView('list')}
                content="Layout Lista"
              />
              <PopupButton
                icon="block layout"
                active={this.props.viewType === 'block'}
                onClick={e => this.changeGridView('block')}
                content="Layout due colonne"
              />
              <PopupButton
                icon="grid layout"
                active={this.props.viewType === 'grid'}
                onClick={e => this.changeGridView('grid')}
                content="Layout griglia"
              />
            </Button.Group>
          </div>
          {this.props.isAdmin && (
            <div style={{ marginTop: '2ex' }}>
              <Button.Group vertical>
                <PopupButton
                  icon="unhide"
                  active={!this.props.activesOnly}
                  onClick={e => this.setActiveFilterHandler(false)}
                  content="Mostra tutte"
                />
                <PopupButton
                  icon="hide"
                  active={this.props.activesOnly}
                  onClick={e => this.setActiveFilterHandler(true)}
                  content="Mostra solo attive"
                />
              </Button.Group>
            </div>
          )}
        </div>

        {/* MAIN CONTENT - GAMPAIGN GRID */}
        <div style={{ height: '100vh', overflow: 'auto' }}>
          <Dimmer.Dimmable as={Container} dimmed={this.props.isLoading} style={{ height: '100vh' }}>
            <Dimmer active={this.props.isLoading} inverted style={{ zIndex: 850 }}>
              <Loader />
            </Dimmer>

            <Modal
              open={this.props.dropPending !== ''}
              header={<Header icon="erase" content="Conferma cancellazione campagna" />}
              content={
                <span>
                  Cancellare la campagna <b>{this.props.dropPending}</b>?
                  <br />
                  Attenzione: questa operazione è irreversibile
                  <br />
                  {this.props.currentUser === 'admin' && (
                  "Verranno cancellati tutti i medici e le rispettive visite caricate."
                  )}
                </span>
              }
              onCancel={this.onDeleteCancel}
              onConfirm={this.onDeleteConfirm}
            />

            <div style={{ paddingTop: '9ex' }}>
              {cards.length === 0 && <h3 style={{ textAlign: 'center', margin: '6ex 0' }}>Nessuna campagna attiva</h3>}
              <Grid stackable columns={columns}>
                {cards}
                {this.props.isAdmin && <GhostCampaignCard onCommit={this.onCommit} viewType={this.props.viewType} />}
              </Grid>
            </div>
          </Dimmer.Dimmable>
        </div>
      </div>
    )
  }
}

const connector: Connector<OwnProps, MappedProps> = connect(mapStateToProps, null)
export default connector(CampaignGrid)



// WEBPACK FOOTER //
// ./src/campaigns/campaigns-grid.js